import React, { useContext } from 'react';
import { AuthContext } from '../contexts/AuthContext';
import type { AuthContextType, UserRole } from '../types/auth';

// Re-export the main auth hook from context
export { useAuth } from '../contexts/AuthContext';

// Additional auth-related hooks
export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};

// Hook for checking user permissions
export const usePermissions = () => {
  const { user, isAuthenticated, logout } = useAuthContext();

  // Emergency fallback: if user is authenticated but has no role, force re-authentication
  React.useEffect(() => {
    if (isAuthenticated && user && !user.role && !user.role_info?.value) {
      console.error('User is authenticated but has no role information. Forcing re-authentication.');
      logout();
    }
  }, [isAuthenticated, user, logout]);

  const hasRole = (role: UserRole): boolean => {
    if (!isAuthenticated || !user) {
      if (import.meta.env.DEV) {
        console.log('hasRole check failed:', { isAuthenticated, hasUser: !!user, requestedRole: role });
      }
      return false;
    }
    const userRole = user.role_info?.value || user.role;
    const result = userRole === role;
    if (import.meta.env.DEV && !result) {
      console.log('hasRole check:', { userRole, requestedRole: role, result });
    }
    return result;
  };

  const hasAnyRole = (roles: UserRole[]): boolean => {
    if (!isAuthenticated || !user) {
      if (import.meta.env.DEV) {
        console.log('hasAnyRole check failed:', { isAuthenticated, hasUser: !!user, requestedRoles: roles });
      }
      return false;
    }
    const userRole = user.role_info?.value || user.role;
    const result = roles.includes(userRole as UserRole);
    if (import.meta.env.DEV) {
      console.log('hasAnyRole check:', {
        userRole,
        requestedRoles: roles,
        result,
        user_role_info: user.role_info,
        user_role: user.role,
        full_user: user
      });
    }
    return result;
  };

  const isAdmin = (): boolean => {
    return hasAnyRole(['super_admin', 'admin']);
  };

  const isTeacher = (): boolean => {
    return hasRole('teacher');
  };

  const isParent = (): boolean => {
    return hasRole('parent');
  };

  const isOperator = (): boolean => {
    return hasRole('operator');
  };

  const isViewer = (): boolean => {
    return hasRole('viewer');
  };

  const canAccessAdminPanel = (): boolean => {
    return hasAnyRole(['super_admin', 'admin', 'teacher', 'operator']);
  };

  const canManageStudents = (): boolean => {
    return hasAnyRole(['super_admin', 'admin', 'teacher']);
  };

  const canManageCameras = (): boolean => {
    return hasAnyRole(['super_admin', 'admin', 'operator']);
  };

  const canViewReports = (): boolean => {
    return hasAnyRole(['super_admin', 'admin', 'teacher']);
  };

  const canManageUsers = (): boolean => {
    return hasAnyRole(['super_admin', 'admin']);
  };

  const canAccessSettings = (): boolean => {
    return hasAnyRole(['super_admin', 'admin']);
  };

  return {
    hasRole,
    hasAnyRole,
    isAdmin,
    isTeacher,
    isParent,
    isOperator,
    isViewer,
    canAccessAdminPanel,
    canManageStudents,
    canManageCameras,
    canViewReports,
    canManageUsers,
    canAccessSettings,
    currentRole: (user?.role_info?.value || user?.role) as UserRole,
    roleLabel: user?.role_info?.label,
  };
};

// Hook for user profile information
export const useUserProfile = () => {
  const { user, isAuthenticated } = useAuthContext();

  const getDisplayName = (): string => {
    if (!user) return '';
    return user.full_name || user.email || 'User';
  };

  const getInitials = (): string => {
    if (!user) return '';
    const name = user.full_name || user.email || 'User';
    return name
      .split(' ')
      .map(word => word.charAt(0).toUpperCase())
      .slice(0, 2)
      .join('');
  };

  const getSchoolInfo = () => {
    return user?.school_info || null;
  };

  const getTeachingInfo = () => {
    return user?.teaching_assignments || null;
  };

  const isActive = (): boolean => {
    return user?.account_status?.active || user?.active || false;
  };

  const getLastSignIn = (): string | null => {
    return user?.account_status?.last_sign_in || user?.last_sign_in_at || null;
  };

  const getSignInCount = (): number => {
    return user?.account_status?.sign_in_count || user?.sign_in_count || 0;
  };

  return {
    user,
    isAuthenticated,
    getDisplayName,
    getInitials,
    getSchoolInfo,
    getTeachingInfo,
    isActive,
    getLastSignIn,
    getSignInCount,
    email: user?.email,
    role: user?.role_info,
  };
};

// Hook for authentication loading states
export const useAuthLoading = () => {
  const { isLoading, error } = useAuthContext();

  return {
    isLoading,
    hasError: !!error,
    error,
  };
};

// Hook for authentication actions
export const useAuthActions = () => {
  const { login, logout, refreshToken, clearError } = useAuthContext();

  return {
    login,
    logout,
    refreshToken,
    clearError,
  };
};
